import { get, post } from './client';

export interface KlaviyoCredentials {
  api_key?: string;
  base_url?: string;
  clear_credentials?: boolean;
}

export interface KlaviyoConnectionStatus {
  klaviyo_configured: boolean;
  klaviyo_base_url: string | null;
  klaviyo_last_synced_at: string | null;
}

export interface KlaviyoTestResponse {
  status: string;
  connected: boolean;
  sample_count?: number;
}

export interface KlaviyoCollectionItem {
  id: string;
  name: string | null;
  status: string | null;
  archived: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  channels: string[];
  // Campaign-specific fields (live from Klaviyo API)
  subject_line?: string | null;
  send_time?: string | null;
  metrics?: {
    open_rate: number | null;
    click_rate: number | null;
    conversion_rate: number | null;
    total_recipients: number | null;
    unique_opens: number | null;
    unique_clicks: number | null;
    revenue_per_recipient: number | null;
    last_refreshed_at: string | null;
  } | null;
  analysis?: {
    analysis_status: string | null;
    total_actions: number | null;
    email_actions: number | null;
    delay_actions: number | null;
    branch_actions: number | null;
    summary: string | null;
    error: string | null;
    last_analyzed_at: string | null;
  } | null;
}

export interface KlaviyoCollectionResponse {
  items: KlaviyoCollectionItem[];
  next_cursor: string | null;
  meta?: {
    source?: string;
    from_cache?: boolean;
    total_cached?: number;
    sync_running?: boolean;
    background_sync_started?: boolean;
    stale?: boolean;
    last_synced_at?: string | null;
    sync_error?: string | null;
    lookback_days?: number;
    date_range_start?: string | null;
    exclude_drafts?: boolean;
  };
}

export async function updateKlaviyoCredentials(
  teamId: string,
  credentials: KlaviyoCredentials
): Promise<KlaviyoConnectionStatus> {
  return post<KlaviyoConnectionStatus>(`/api/teams/${teamId}/klaviyo/credentials`, credentials);
}

export async function testKlaviyoConnection(teamId: string): Promise<KlaviyoTestResponse> {
  return get<KlaviyoTestResponse>(`/api/teams/${teamId}/klaviyo/test`);
}

export async function listKlaviyoCampaigns(
  teamId: string,
  pageSize: number = 100,
  cursor?: string,
  lookbackDays: number = 180,
  excludeDrafts: boolean = true
): Promise<KlaviyoCollectionResponse> {
  const params = new URLSearchParams({
    page_size: String(pageSize),
    lookback_days: String(lookbackDays),
    exclude_drafts: String(excludeDrafts),
  });
  if (cursor) params.set('cursor', cursor);
  return get<KlaviyoCollectionResponse>(`/api/teams/${teamId}/klaviyo/campaigns?${params.toString()}`);
}

export async function listKlaviyoFlows(
  teamId: string,
  pageSize: number = 50,
  cursor?: string
): Promise<KlaviyoCollectionResponse> {
  const params = cursor ? `?page_size=${pageSize}&cursor=${encodeURIComponent(cursor)}` : `?page_size=${pageSize}`;
  return get<KlaviyoCollectionResponse>(`/api/teams/${teamId}/klaviyo/flows${params}`);
}

export interface KlaviyoListItem {
  id: string;
  name: string | null;
  type: 'list' | 'segment' | string;
  profile_count: number | null;
  created_at: string | null;
  updated_at: string | null;
  last_sync_at: string | null;
}

export interface KlaviyoListsResponse {
  items: KlaviyoListItem[];
  next_cursor: string | null;
  meta?: KlaviyoCollectionResponse['meta'];
}

export async function listKlaviyoLists(
  teamId: string,
  pageSize: number = 50,
  cursor?: string
): Promise<KlaviyoListsResponse> {
  const params = cursor ? `?page_size=${pageSize}&cursor=${encodeURIComponent(cursor)}` : `?page_size=${pageSize}`;
  return get<KlaviyoListsResponse>(`/api/teams/${teamId}/klaviyo/lists${params}`);
}

export interface KlaviyoAudienceCohort {
  cohort_month: string;
  joined_count: number;
  subscribed_count: number;
  unsubscribed_count: number;
  never_subscribed_count: number;
  has_subscription_data: boolean;
  last_sync_at: string | null;
}

export interface KlaviyoCohortSource {
  cohort_month: string;
  source: string;
  count: number;
  subscribed_count: number;
  unsubscribed_count: number;
  never_subscribed_count: number;
  last_sync_at: string | null;
}

export interface KlaviyoCohortChurn {
  cohort_month: string;
  churn_month: string;
  count: number;
  last_sync_at: string | null;
}

export interface KlaviyoAudienceCohortsResponse {
  cohorts: KlaviyoAudienceCohort[];
  sources: KlaviyoCohortSource[];
  churn: KlaviyoCohortChurn[];
  meta?: KlaviyoCollectionResponse['meta'] & {
    source_rows_cached?: number;
    churn_rows_cached?: number;
    cache_miss_sync?: unknown;
    sync_progress?: {
      status: string | null;
      processed_profiles: number;
      next_cursor: string | null;
      pages_processed: number;
      last_error: string | null;
    };
  };
}

export async function getKlaviyoAudienceCohorts(
  teamId: string
): Promise<KlaviyoAudienceCohortsResponse> {
  return get<KlaviyoAudienceCohortsResponse>(`/api/teams/${teamId}/klaviyo/audience-cohorts`);
}

export interface KlaviyoCampaignContent {
  campaign_id: string;
  message_id?: string | null;
  template_id?: string | null;
  name: string | null;
  channel?: string | null;
  subject_line: string | null;
  send_time: string | null;
  html: string | null;
  html_hash?: string | null;
  template_status?: string | null;
  last_sync_at?: string | null;
  source?: string | null;
}

export interface KlaviyoEmailTemplatesResponse {
  templates: KlaviyoCampaignContent[];
  count: number;
}

export async function getCampaignContent(
  teamId: string,
  campaignId: string
): Promise<KlaviyoCampaignContent> {
  return get<KlaviyoCampaignContent>(`/api/teams/${teamId}/klaviyo/campaigns/${campaignId}/content`);
}

export async function getEmailTemplates(
  teamId: string,
  limit: number = 5
): Promise<KlaviyoEmailTemplatesResponse> {
  return get<KlaviyoEmailTemplatesResponse>(`/api/teams/${teamId}/klaviyo/email-templates?limit=${limit}`);
}
